import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { profileApi } from '../services/api';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation('app');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: user?.name || '', email: user?.email || '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await profileApi.updateProfile(formData);
      await useAuthStore.getState().getCurrentUser();
      setIsEditing(false);
      alert(t('profile.updateSuccess'));
    } catch (error: any) {
      alert(error.response?.data?.message || t('profile.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <UserCircleIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-50">{t('profile.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('profile.subtitle')}</p>
          </div>
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={labelCls}>{t('profile.name')}</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputCls} required />
            </div>
            <div>
              <label className={labelCls}>{t('profile.email')}</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} required />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setIsEditing(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                {t('profile.cancel')}
              </button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 cursor-pointer">
                {loading ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('profile.name')}</label>
                <p className="text-gray-900 dark:text-gray-100">{user?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('profile.email')}</label>
                <p className="text-gray-900 dark:text-gray-100">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{t('profile.role')}</label>
                <p className="text-gray-900 dark:text-gray-100">{user?.role}</p>
              </div>
            </div>
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
              {t('profile.edit')}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">{t('profile.security')}</h2>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex justify-between items-center">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{t('profile.changePassword')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('profile.changePasswordDesc')}</p>
          </div>
          <button onClick={() => navigate('/change-password')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer">
            <KeyIcon className="w-4 h-4" />
            {t('profile.changePassword')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
